import {
  ArrowUpRight,
  CalendarClock,
  ClipboardList,
  CreditCard,
  FileText,
  HeartHandshake,
  Link2,
  MessageCircleHeart,
  ReceiptText,
  Video,
  Waves,
} from 'lucide-react';
import Payments from '../components/Payments';
import SEO from '../components/SEO';
import PageHeader from '../components/PageHeader';
import { MaskedLinesOnView, ParallaxImage, Reveal } from '../components/Reveal';
import { EMAIL, IMG, JANE_URL, PHONE, PHONE_HREF } from '../data/site';

const INCLUDES = [
  {
    icon: ClipboardList,
    title: 'Birth planning sessions',
    text: 'One-on-one video sessions to map your preferences, your questions, and your plan — before the big day asks for answers.',
  },
  {
    icon: Waves,
    title: 'Comfort-measures coaching',
    text: 'Breath, positioning, and counter-pressure techniques your partner can actually remember and use in the moment.',
  },
  {
    icon: MessageCircleHeart,
    title: 'Postpartum preparation',
    text: 'Feeding basics, recovery expectations, and a realistic plan for the fourth trimester — built before the baby arrives.',
  },
  {
    icon: MessageCircleHeart,
    title: 'Birth debrief & processing',
    text: 'Space to make sense of how your birth went — gentle, structured conversation that helps the story settle.',
  },
];

export default function VirtualDoula() {
  return (
    <>
      <SEO
        title="Virtual Doula Support | Thula Doula Wellness — Humble, TX"
        description="Virtual-only doula support: birth planning, comfort-measures coaching, and postpartum preparation over secure video. Currently booked through May 2027."
      />
      <PageHeader
        testId="doula-page-header"
        eyebrow="Virtual Doula Support"
        lines={['Steady guidance,', 'from anywhere.']}
        sub="Doula care for pregnancy, birth, and postpartum delivered entirely by secure video — so you can prepare from your own living room."
      />

      <section className="bg-brand-cream pb-8" data-testid="doula-availability">
        <div className="mx-auto max-w-7xl px-5 md:px-10">
          <Reveal>
            <div
              data-testid="doula-availability-banner"
              className="flex flex-col items-start gap-5 rounded-3xl border border-brand-teal/15 bg-brand-sage/40 p-7 md:flex-row md:items-center md:justify-between md:p-9"
            >
              <div className="flex flex-col gap-2 md:flex-row md:items-center md:gap-5">
                <span
                  data-testid="doula-virtual-only-pill"
                  className="inline-flex w-fit items-center gap-2 rounded-full bg-brand-cream px-4 py-2 text-[11px] font-bold uppercase tracking-[0.18em] text-brand-teal"
                >
                  <Video className="size-4" /> Virtual-only at this time
                </span>
                <p className="text-sm font-medium text-brand-ink md:text-base">
                  <CalendarClock className="mr-2 inline size-4 text-brand-teal" />
                  Currently <strong data-testid="doula-booked-through">booked through May 2027</strong> —
                  join the waitlist below.
                </p>
              </div>
              <a
                data-testid="doula-waitlist-btn"
                href={`mailto:${EMAIL}?subject=Virtual%20doula%20waitlist`}
                className="group inline-flex items-center gap-2 rounded-full bg-brand-teal px-6 py-3 text-sm font-semibold text-brand-cream transition-[background-color,transform] duration-300 hover:bg-brand-ink active:scale-[0.97]"
              >
                Join the waitlist
                <ArrowUpRight className="size-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-brand-cream py-16 md:py-24" data-testid="doula-includes">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-5 md:px-10 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <MaskedLinesOnView
              as="h2"
              lines={['What virtual', 'doula care includes.']}
              className="font-display text-4xl font-light leading-[1.08] tracking-tight text-brand-ink md:text-5xl"
            />
            <Reveal delay={0.15}>
              <p className="mt-7 max-w-md text-base leading-relaxed text-brand-moss">
                The heart of doula work is presence, not proximity. Over 60-minute video sessions —
                scheduled around your trimester and your energy — you get the same listening,
                preparation, and steady reassurance that in-person doula care provides.
              </p>
            </Reveal>
            <Reveal delay={0.25}>
              <ParallaxImage
                src={IMG.doula}
                alt="A mother holding her newborn"
                className="mt-10 aspect-[4/3] w-full rounded-3xl"
                strength={26}
              />
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              {INCLUDES.map((item, i) => (
                <Reveal key={item.title} delay={i * 0.1}>
                  <div className="h-full rounded-3xl bg-brand-parchment p-8 transition-transform duration-500 hover:-translate-y-1">
                    <item.icon className="size-7 text-brand-teal" strokeWidth={1.5} />
                    <h3 className="mt-5 font-display text-2xl font-light tracking-tight text-brand-ink">
                      {item.title}
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed text-brand-moss">{item.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-brand-cream pb-16 md:pb-24" data-testid="payments-doula">
        <div className="mx-auto max-w-7xl px-5 md:px-10">
          <Payments
            testId="payments-doula"
            title="Paying for doula care."
            methods={[
              { icon: FileText, label: 'Invoicing' },
              { icon: Link2, label: 'Secure payment links' },
              { icon: CreditCard, label: 'Major credit & debit cards' },
              { icon: ReceiptText, label: 'Checks' },
              { icon: HeartHandshake, label: 'HSA & FSA cards' },
            ]}
            notes={[
              'Virtual doula care is private-pay. Settle your invoice however suits you best — by card, check, or a secure payment link sent straight to your inbox.',
            ]}
          />
        </div>
      </section>

      <section className="bg-brand-cream pb-24 md:pb-32" data-testid="doula-cta">
        <div className="mx-auto max-w-7xl px-5 md:px-10">
          <div className="relative overflow-hidden rounded-3xl bg-brand-ink p-8 text-brand-cream md:p-14">
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -left-24 -bottom-24 size-72 rounded-full bg-brand-teal/40 blur-3xl"
            />
            <div className="relative grid grid-cols-1 items-center gap-10 lg:grid-cols-12">
              <div className="lg:col-span-7">
                <MaskedLinesOnView
                  as="h2"
                  lines={['Join the waitlist,', 'or see us on the table.']}
                  className="font-display text-4xl font-light leading-[1.08] tracking-tight md:text-5xl"
                />
                <p className="mt-5 max-w-xl text-sm leading-relaxed text-brand-cream/75 md:text-base">
                  Doula availability opens after May 2027 — email {EMAIL} with your due month to
                  hold a place. Meanwhile, massage appointments are available now.
                </p>
              </div>
              <div className="flex flex-col items-start gap-4 lg:col-span-5 lg:items-end">
                <a
                  data-testid="doula-cta-book-btn"
                  href={JANE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center gap-2.5 rounded-full bg-brand-cream px-8 py-4 text-base font-semibold text-brand-ink transition-[background-color,transform] duration-300 hover:bg-brand-sage active:scale-[0.97]"
                >
                  Book a Massage <ArrowUpRight className="size-5" />
                </a>
                <a
                  data-testid="doula-cta-phone-link"
                  href={PHONE_HREF}
                  className="text-sm text-brand-cream/60 transition-colors duration-300 hover:text-brand-cream"
                >
                  Questions? Call {PHONE}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
